import React, { useState, useCallback, useRef, useEffect } from "react";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import CommonBanner from "./components/CommonBanner";
import PlanCard from "./components/PlanCard";
import AddonCard from "./components/AddonCard";
import CartDrawer from "./components/CartDrawer";
import EnrollModal from "./components/EnrollModal";
import ThankYouScreen from "./components/ThankYouScreen";
import FAB from "./components/FAB";
import Toast from "./components/Toast";
import { PLANS, ADDONS } from "./data/plansData";
import { toast as toaser } from "react-toastify";


// ── localStorage helpers ──
function loadLS(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch { return fallback; }
}
function saveLS(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

function safeJsonParse(value, fallback) {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function normalizeOrderData(raw) {
  const purchases = Array.isArray(raw?.purchases) ? raw.purchases : [];
  const skusFromPurchases = purchases
    .map((p) => p?.sku)
    .filter(Boolean);
  const skus = Array.isArray(raw?.skus)
    ? raw.skus
    : skusFromPurchases;

  return {
    orderId: raw?.orderId || raw?.order_id || "IADVL-" + Date.now(),
    payId: raw?.payId || raw?.payment_id || "—",
    name: raw?.name || "Member",
    memid: raw?.memid || raw?.membership_id || "",
    email: raw?.email || "",
    baseAmount: Number(raw?.baseAmount || raw?.base_amount || 0),
    gstAmount: Number(raw?.gstAmount || raw?.gst_amount || 0),
    grandTotal: Number(raw?.grandTotal || raw?.grand_total || 0),
    plans: raw?.plans || "",
    mobile: raw?.mobile || raw?.phone || "",
    purchases,
    member_details_id: raw?.member_details_id || raw?.member_id || "",
    payment_request_id: raw?.payment_request_id || raw?.payment_id || "",
    skus,
    sku: raw?.sku || skus[0] || "",
  };
}

function toBase64Url(bytes) {
  const str = btoa(String.fromCharCode(...new Uint8Array(bytes)));
  return str.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(base64url) {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

async function deriveAesKey(secret) {
  const enc = new TextEncoder();
  const secretBytes = enc.encode(secret);
  const hash = await window.crypto.subtle.digest("SHA-256", secretBytes);
  return window.crypto.subtle.importKey("raw", hash, { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
}

async function encryptPayload(payload, secret) {
  if (!secret || !window.crypto?.subtle) return null;
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveAesKey(secret);
  const data = new TextEncoder().encode(JSON.stringify(payload));
  const cipher = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
  return `${toBase64Url(iv)}.${toBase64Url(cipher)}`;
}

async function decryptPayload(token, secret) {
  if (!token || !secret || !window.crypto?.subtle) return null;
  try {
    const [ivPart, cipherPart] = token.split(".");
    if (!ivPart || !cipherPart) return null;
    const iv = fromBase64Url(ivPart);
    const cipher = fromBase64Url(cipherPart);
    const key = await deriveAesKey(secret);
    const plain = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      cipher,
    );
    return safeJsonParse(new TextDecoder().decode(plain), null);
  } catch {
    return null;
  }
}

async function buildOrderDataFromQuery(search, secret) {
  const params = new URLSearchParams(search);
  const shouldOpen =
    params.get("activate") === "1" || params.get("thankyou") === "1";
  if (!shouldOpen) return null;

  const encryptedToken = params.get("data");
  if (encryptedToken) {
    const decrypted = await decryptPayload(encryptedToken, secret);
    if (decrypted && typeof decrypted === "object") {
      return normalizeOrderData(decrypted);
    }
  }

  const payload = safeJsonParse(params.get("payload"), null);
  if (payload && typeof payload === "object") {
    return normalizeOrderData(payload);
  }

  return normalizeOrderData({
    orderId: params.get("orderId") || params.get("order_id") || "IADVL-" + Date.now(),
    payId: params.get("payId") || params.get("payment_id") || "—",
    name: params.get("name") || "Member",
    memid: params.get("memid") || params.get("membership_id") || "",
    email: params.get("email") || "",
    mobile: params.get("mobile") || params.get("phone") || "",
    baseAmount: Number(params.get("baseAmount") || 0),
    gstAmount: Number(params.get("gstAmount") || 0),
    grandTotal: Number(params.get("grandTotal") || 0),
    plans: params.get("plans") || "",
    purchases: safeJsonParse(params.get("purchases"), []),
  });
}

function buildActivationUrl(token) {
  const baseUrl = `${window.location.origin}${window.location.pathname}`;
  return `${baseUrl}?activate=1&data=${encodeURIComponent(token || "")}`;
}
// ── One-time migration: strip duplicate "Family Add-on: " prefix ──
function migrateCart(cart) {
  const fixed = {};
  Object.entries(cart).forEach(([id, item]) => {
    fixed[id] = {
      ...item,
      name: item.name.replace(/^Family Add-on:\s+Family Add-on:\s*/i, "Family Add-on: "),
    };
  });
  return fixed;
}

const DEFAULT_QTYS = { p1: 1, p2: 1, p3: 1, p4: 1, a1: 1, a2: 1, a3: 1 };
const API_RNR_BASE_URL = process.env.REACT_APP_API_RNR_BASE_URL;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const RNR_API_KEY = process.env.REACT_APP_RNR_API_KEY;
const RNR_API_SECRET = process.env.REACT_APP_RNR_API_SECRET;
const ACTIVATION_SECRET = process.env.REACT_APP_ACTIVATION_SECRET || "";

export default function App() {
  // ── Cart state (persisted to localStorage) ──
  const [cart, setCart] = useState(() => migrateCart(loadLS("iadvl_cart", {})));
  const [mrpMap, setMrpMap] = useState(() => loadLS("iadvl_mrpMap", {}));
  const [qtys, setQtys] = useState(() => loadLS("iadvl_qtys", DEFAULT_QTYS));
  const [skuPriceMap, setSkuPriceMap] = useState(() => loadLS("iadvl_skuPriceMap", {}));

  // Sync to localStorage whenever they change
  // useEffect(() => { saveLS("iadvl_cart", {...cart}); }, [cart]);
  // Sync cart prices whenever live SKU prices are updated
  useEffect(() => {
    if (Object.keys(skuPriceMap).length > 0) {
      setCart((prevCart) => {
        const updatedCart = { ...prevCart };
        let hasChanged = false;

        Object.keys(updatedCart).forEach((id) => {
          const item = updatedCart[id];
          const freshPrice = skuPriceMap[item.sku];

          if (freshPrice !== undefined && freshPrice !== item.price) {
            updatedCart[id] = { ...item, price: freshPrice };
            hasChanged = true;
          }
        });

        return hasChanged ? updatedCart : prevCart;
      });
    }
  }, [skuPriceMap,cart]);

  useEffect(() => { saveLS("iadvl_mrpMap", mrpMap); }, [mrpMap]);
  useEffect(() => { saveLS("iadvl_qtys", qtys); }, [qtys]);
  useEffect(() => { saveLS("iadvl_skuPriceMap", skuPriceMap); }, [skuPriceMap]);

  // Fetch live SKU prices (by sku_id)
  useEffect(() => {
    let cancelled = false;

    async function fetchSkus() {
      if (!RNR_API_KEY || !RNR_API_SECRET) {
        console.warn("SKU API key/secret missing. Set REACT_APP_SKU_API_KEY and REACT_APP_SKU_API_SECRET.");
        return;
      }

      try {
        const res = await fetch(`${API_RNR_BASE_URL}/skus?status=active`, {
          method: "GET",
          headers: {
            "X-API-Key": RNR_API_KEY,
            "X-API-Secret": RNR_API_SECRET,
          },
        });
        const json = await res.json();
        const skus = json?.data?.skus || [];

        const nextMap = {};
        skus.forEach((s) => {
          const id = s?.sku_id;
          const price = s?.price;
          const n = Number(price);
          if (id && Number.isFinite(n)) nextMap[id] = n;
        });

        if (!cancelled && Object.keys(nextMap).length) {
          setSkuPriceMap(nextMap);
        }
      } catch (e) {
        console.error("Failed to fetch SKU prices", e);
      }
    }

    fetchSkus();
    return () => {
      cancelled = true;
    };
  }, []);

  const plansWithSkuPrices = PLANS.map((p) => ({
    ...p,
    price: skuPriceMap[p.sku] ?? p.price,
  }));

  const addonsWithSkuPrices = ADDONS.map((a) => ({
    ...a,
    price: skuPriceMap[a.sku] ?? a.price,
  }));

  // ── UI state ──
  const [cartOpen, setCartOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [tyOpen, setTyOpen] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [toast, setToast] = useState({ show: false, msg: "" });
  const [paymentSuccessLoading, setPaymentSuccessLoading] = useState(false);
  const [activationOtpOpen, setActivationOtpOpen] = useState(false);
  const [activationOtpSending, setActivationOtpSending] = useState(false);
  const [activationOtpVerifying, setActivationOtpVerifying] = useState(false);
  const [activationOtpError, setActivationOtpError] = useState("");
  const [activationOtpSent, setActivationOtpSent] = useState(false);
  const [activationOtpValues, setActivationOtpValues] = useState(["", "", "", "", "", ""]);
  const [activationOtpCooldown, setActivationOtpCooldown] = useState(0);
  const [pendingOrderData, setPendingOrderData] = useState(null);
  const [activationSessionKey, setActivationSessionKey] = useState("");
  const toastTimer = useRef(null);
  const activationOtpRefs = useRef([]);

  const sendActivationOtp = useCallback(
    async (mobile) => {
      if (!API_BASE_URL || !mobile) {
        setActivationOtpError("Mobile number is missing for OTP verification.");
        return false;
      }

      setActivationOtpSending(true);
      setActivationOtpError("");
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify({ mobile: String(mobile) }),
        redirect: "follow",
      };

      try {
        const response = await fetch(`${API_BASE_URL}/send_otp_activation`, requestOptions);
        const text = await response.text();
        const result = safeJsonParse(text, null);
        if (result?.data?.status !== "success") {
          setActivationOtpError("Unable to send OTP. Please try again.");
          return false;
        }
        setActivationOtpSent(true);
        setActivationOtpValues(["", "", "", "", "", ""]);
        setActivationOtpCooldown(30);
        setTimeout(() => activationOtpRefs.current[0]?.focus(), 80);
        return true;
      } catch (error) {
        console.error("send_otp_activation API error", error);
        setActivationOtpError("Unable to send OTP. Please try again.");
        return false;
      } finally {
        setActivationOtpSending(false);
      }
    },
    [],
  );

  const verifyActivationOtp = useCallback(
    async (mobile, otp) => {
      if (!API_BASE_URL || !mobile || otp.length !== 6) {
        setActivationOtpError("Please enter a valid 6-digit OTP.");
        return false;
      }

      setActivationOtpVerifying(true);
      setActivationOtpError("");
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify({'id':1, mobile: String(mobile), otp }),
        redirect: "follow",
      };

      try {
        const primaryResponse = await fetch(`${API_BASE_URL}/verify_otp_activation`, requestOptions);
        const primaryText = await primaryResponse.text();
        const primaryResult = safeJsonParse(primaryText, null);
        return primaryResult?.data?.status === "success";
      } catch (error) {
        console.error("verify_otp_activation API error", error);
        return false;
      } finally {
        setActivationOtpVerifying(false);
      }
    },
    [],
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const orderFromLink = await buildOrderDataFromQuery(
        window.location.search,
        ACTIVATION_SECRET,
      );
      if (!mounted || !orderFromLink) return;
      const cameFromActivationLink =
        (params.get("activate") === "1" || params.get("thankyou") === "1") &&
        !!(params.get("data") || params.get("payload"));

      if (!cameFromActivationLink) {
        setOrderData(orderFromLink);
        setTyOpen(true);
        return;
      }

      const uniqueToken = params.get("data") || orderFromLink.orderId || "";
      const sessionKey = `iadvl_activation_otp_verified_${uniqueToken}`;
      const alreadyVerified = sessionStorage.getItem(sessionKey) === "1";
      if (alreadyVerified) {
        setOrderData(orderFromLink);
        setTyOpen(true);
        return;
      }

      setPendingOrderData(orderFromLink);
      setActivationSessionKey(sessionKey);
      setActivationOtpOpen(true);
      await sendActivationOtp(orderFromLink.mobile);
    })();
    return () => {
      mounted = false;
    };
  }, [sendActivationOtp]);

  const handleActivationOtpChange = useCallback((idx, value) => {
    const nextValue = value.replace(/\D/g, "").slice(0, 1);
    setActivationOtpValues((prev) => {
      const next = [...prev];
      next[idx] = nextValue;
      return next;
    });
    if (nextValue && idx < 5) activationOtpRefs.current[idx + 1]?.focus();
  }, []);

  const handleActivationOtpKeyDown = useCallback((idx, event) => {
    if (event.key === "Backspace" && !activationOtpValues[idx] && idx > 0) {
      activationOtpRefs.current[idx - 1]?.focus();
    }
  }, [activationOtpValues]);

  const handleActivationOtpVerify = useCallback(async () => {
    const otp = activationOtpValues.join("");
    const mobile = pendingOrderData?.mobile || "";
    const isValid = await verifyActivationOtp(mobile, otp);
    if (!isValid) {
      setActivationOtpError("Invalid OTP. Please try again.");
      return;
    }

    if (activationSessionKey) {
      sessionStorage.setItem(activationSessionKey, "1");
    }
    setActivationOtpOpen(false);
    setActivationOtpError("");
    setActivationOtpSent(false);
    setActivationOtpValues(["", "", "", "", "", ""]);
    setOrderData(pendingOrderData);
    setTyOpen(true);
  }, [activationOtpValues, pendingOrderData, verifyActivationOtp, activationSessionKey]);

  useEffect(() => {
    if (activationOtpCooldown <= 0) return;
    const timer = setInterval(() => {
      setActivationOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [activationOtpCooldown]);

  // ── Cart totals ──
  const cartCount = Object.values(cart).reduce((a, c) => a + c.qty, 0);

  // ── Handlers ──
  const showToast = useCallback((msg) => {
    clearTimeout(toastTimer.current);
    setToast({ show: true, msg });
    toastTimer.current = setTimeout(
      () => setToast({ show: false, msg: "" }),
      3000,
    );
  }, []);

  const changeQty = useCallback((id, delta) => {
    setQtys((prev) => {
      const next = Math.max(1, Math.min(20, (prev[id] || 1) + delta));
      return { ...prev, [id]: next };
    });
    // if already in cart, update cart qty too
    setCart((prev) => {
      if (!prev[id]) return prev;
      const next = Math.max(1, Math.min(20, prev[id].qty + delta));
      return { ...prev, [id]: { ...prev[id], qty: next } };
    });
  }, []);

  const addPlan = useCallback(
    (id, name, price, mrp, sku, nameSub = "") => {
      const qty = qtys[id] || 1;
      setCart((prev) => ({ ...prev, [id]: { name, nameSub, price, qty, sku } }));
      setMrpMap((prev) => ({ ...prev, [id]: mrp }));
      showToast(`✓  ${name}${nameSub ? " " + nameSub : ""} added to cart`);
    },
    [qtys, showToast],
  );

  const removeFromCart = useCallback((id) => {
    setCart((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
    setMrpMap((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  }, []);

  const startEnroll = useCallback(() => {
    if (Object.keys(cart).length === 0) return;
    setCartOpen(false);
    setEnrollOpen(true);
  }, [cart]);

  const createOrder = async (formData) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("X-API-Key", RNR_API_KEY);
    myHeaders.append("X-API-Secret", RNR_API_SECRET);
    myHeaders.append("X-API-additional-identifier", "1234567890");

    const requests = Object.values(cart).map((item) => {
      const orderData = {
        sku_id: item.sku,
        quantity: item.qty,
        amount: item.price,
        currency: "INR",
        external_order_ref: `EXT-${formData.memid || "IADVL"}-${Date.now()}`,
        customer: {
          name: formData.name,
          email: formData.email,
          phone: formData.mobile,
          country_code: "+91",
        },
        shipping_address: {
          line1: formData.address,
          line2: ",",
          city: formData.city,
          state: ",",
          country: "India",
          pincode: ",",
        },
        billing_address: {
          line1: formData.address,
          line2: ",",
          city: formData.city,
          state: ",",
          country: "India",
          pincode: ",",
        },
      };

      const raw = JSON.stringify(orderData);
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      return fetch(
       `${API_RNR_BASE_URL}/orders`,
        requestOptions,
      )
        .then((response) => response.text())
        .catch((error) => {
          console.error(error);
          return "";
        });
    });

    const responses = await Promise.all(requests);
    return responses;
  };

  const changePaymentStatus = useCallback(async (paymentRequestId, activationUrl, order_id, razorpay_payment_id, razorpay_signature) => {
    if (!paymentRequestId || !API_BASE_URL) return false;

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      payment_request_id: paymentRequestId,
      activation_url: activationUrl || "",
      order_id: order_id || "",
      payment_gateway_payment_id: razorpay_payment_id || "",
      razorpay_signature: razorpay_signature || "",
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}/payment_success`,
        requestOptions,
      );
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        return false;
      }
      return data?.data?.status === "success";
    } catch (error) {
      console.error("change_payment_status API error", error);
      return false;
    }
  }, []);

  const handlePaymentSuccess = useCallback(
    async (response, formData) => {
      setEnrollOpen(false);
      setPaymentSuccessLoading(true);
      const baseAmount = Object.keys(cart).reduce(
        (a, id) => a + cart[id].price * cart[id].qty,
        0,
      );
      const gstAmount  = Math.round(baseAmount * 0.05);
      const grandTotal = baseAmount + gstAmount;
      const plans = Object.values(cart)
        .map((c) => `${c.name}${c.nameSub ? " " + c.nameSub : ""}${c.qty > 1 ? " ×" + c.qty : ""}`)
        .join("\n");
      const purchases = Object.entries(cart).map(([id, item]) => ({
        id,
        title: item.name + (item.nameSub ? " " + item.nameSub : ""),
        sku: item.sku,
        qty: item.qty,
        type: id.startsWith("a") ? "addon" : "primary",
        base_plan_amount: item.price,
        base_plan_gst_amount: item.price +Math.round(item.price * 0.05),
        total_plan_amount: item.price * item.qty,
        total_plan_gst_amount: item.price + Math.round(item.price * item.qty * 0.05),
      }));

      const paymentRequestId =
        formData?.payment_request_id ||
        (() => {
          try {
            return sessionStorage.getItem("iadvl_payment_request_id");
          } catch (_) {
            return null;
          }
        })();
      const activationPayload = {
        orderId: response.razorpay_order_id || "IADVL-" + Date.now(),
        payId: response.razorpay_payment_id || "—",
        name: formData?.name || "",
        memid: formData?.memid || "",
        email: formData?.email || "",
        mobile: formData?.mobile || "",
        baseAmount,
        gstAmount,
        grandTotal,
        plans,
        purchases,
        payment_request_id: paymentRequestId || "",
        member_details_id: formData?.member_details_id || "",
        skus: purchases.map((p) => p.sku).filter(Boolean),
        sku: purchases?.[0]?.sku || "",
      };
      const activationToken = await encryptPayload(activationPayload, ACTIVATION_SECRET);
      const activationUrl = buildActivationUrl(activationToken);

      const changePaymentStatusRes = await changePaymentStatus(
        paymentRequestId,
        activationUrl,
        response.razorpay_order_id || "IADVL-" + Date.now(),
        response.razorpay_payment_id || "—",
        response.razorpay_signature || "—"
      );
      if(changePaymentStatusRes){
        // await createOrder(formData);
        setOrderData({
          orderId: activationPayload.orderId,
          payId: activationPayload.payId,
          name: formData.name,
          memid: formData.memid,
          email: formData.email,
          baseAmount,
          gstAmount,
          grandTotal,
          plans,
          purchases,
          member_details_id: activationPayload.member_details_id,
          payment_request_id: activationPayload.payment_request_id,
          skus: activationPayload.skus,
          sku: activationPayload.sku,
        });
        setTyOpen(true);
        setPaymentSuccessLoading(false);
      }else{
        toaser.error("Something went wrong. Please try again.");
        setPaymentSuccessLoading(false);
      }
    },
    [cart, changePaymentStatus],
  );

  const resetAll = useCallback(() => {
    setTyOpen(false);
    setCart({});
    setMrpMap({});
    saveLS("iadvl_cart", {});
    saveLS("iadvl_mrpMap", {});
    saveLS("iadvl_qtys", DEFAULT_QTYS);
    setQtys(DEFAULT_QTYS);
    setOrderData(null);
    if (window.location.search) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  return (
    <>
      <Nav cartCount={cartCount} onOpenCart={() => setCartOpen(true)} />

      <div className="page">
        {/* ── Hero copy ── */}
        {/* <div className="sec-eyebrow">IADVL Member Exclusive Wellness Plans</div> */}
        <div className="footer-pi-block" style={{ marginBottom: "10px" }}>
            <div className="footer-pi-title">IADVL Presidential Initiative</div>
            <div className="footer-pi-sub">Wellness Plans by
            <img
              src="https://storecdn.goqii.com/media/goqiiweb/assets/images/site-logo.png"
              alt="GOQii"
              style={{ height: "20px" }}
            />
            </div>
          
          </div>
        <div className="sec-title">
          Find your <em>perfect plan.</em>
        </div>
        <div className="sec-sub">
          Every plan includes a shared set of core wellness features. Pick the
          variant that matches your needs — and add family members below.
        </div>

        <CommonBanner />

        {/* ── Plan cards ── */}
        <div className="plans-row">
          {plansWithSkuPrices.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              qty={qtys[plan.id]}
              inCart={!!cart[plan.id]}
              onChangeQty={changeQty}
              onAddPlan={addPlan}
            />
          ))}
        </div>

        <div className="s-div" />

        {/* ── Family add-ons ── */}
        <div className="sec-eyebrow">Family Add-ons</div>
        <div className="sec-title" style={{ marginBottom: 6 }}>
          Wellness for your <span className="teal">whole family.</span>
        </div>
        <div className="sec-sub" style={{ marginBottom: 28 }}>
          Each add-on includes the same core base. Extend to spouse, children or
          parents at exclusive member rates.
        </div>

        <div className="addons-row">
          {addonsWithSkuPrices.map((addon) => (
            <AddonCard
              key={addon.id}
              addon={addon}
              qty={qtys[addon.id]}
              inCart={!!cart[addon.id]}
              onChangeQty={changeQty}
              onAddPlan={addPlan}
            />
          ))}
        </div>
      </div>

      {/* ── Footer ── */}
      <Footer />

      {/* ── Overlays ── */}
      <CartDrawer
        open={cartOpen}
        cart={cart}
        mrpMap={mrpMap}
        onClose={() => setCartOpen(false)}
        onRemove={removeFromCart}
        onStartEnroll={startEnroll}
      />

      <EnrollModal
        open={enrollOpen}
        cart={cart}
        mrpMap={mrpMap}
        onClose={() => setEnrollOpen(false)}
        onPaymentSuccess={handlePaymentSuccess}
        skuPriceMap={skuPriceMap}
      />

      <ThankYouScreen open={tyOpen} orderData={orderData} onReset={resetAll} />

      <FAB count={cartCount} onClick={() => setCartOpen(true)} />
      <Toast show={toast.show} message={toast.msg} />
      {paymentSuccessLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: "18px 22px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                border: "2px solid #e5e7eb",
                borderTopColor: "#E8560A",
                borderRadius: "50%",
                animation: "iadvlSpin 0.8s linear infinite",
              }}
            />
            Updating payment status...
          </div>
        </div>
      )}
      {activationOtpOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 420,
              background: "#fff",
              borderRadius: 14,
              padding: 20,
              boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
              Verify Your Mobile
            </div>
            <div style={{ fontSize: 14, color: "#4b5563", marginBottom: 14 }}>
              OTP is sent to <strong>+91 {pendingOrderData?.mobile || "N/A"}</strong>
            </div>
            {activationOtpError && (
              <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 10 }}>
                {activationOtpError}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[0, 1, 2, 3, 4, 5].map((idx) => (
                <input
                  key={idx}
                  ref={(el) => (activationOtpRefs.current[idx] = el)}
                  value={activationOtpValues[idx]}
                  onChange={(e) => handleActivationOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleActivationOtpKeyDown(idx, e)}
                  maxLength={1}
                  style={{
                    width: 44,
                    height: 44,
                    textAlign: "center",
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    fontSize: 20,
                  }}
                />
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={handleActivationOtpVerify}
                disabled={activationOtpVerifying}
                style={{
                  flex: 1,
                  height: 42,
                  border: "none",
                  borderRadius: 10,
                  background: "#E8560A",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {activationOtpVerifying ? "Verifying..." : "Verify OTP"}
              </button>
              <button
                onClick={() => sendActivationOtp(pendingOrderData?.mobile || "")}
                disabled={activationOtpSending || activationOtpCooldown > 0}
                style={{
                  height: 42,
                  padding: "0 14px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  color: "#111827",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {activationOtpSending
                  ? "Sending..."
                  : activationOtpCooldown > 0
                  ? `Resend in ${activationOtpCooldown}s`
                  : activationOtpSent
                  ? "Resend OTP"
                  : "Send OTP"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
